import { Checkbox, Chip } from '@heroui/react'
import type { ReactNode } from 'react'
import { useStaffText } from '../ui/StaffText'
import { Notice } from './heroParts'
import type { StaffKey } from '../lib/staffText'
import {
  shownTick,
  succeeded,
  type BranchPlan,
  type BranchStatus,
  type DesignBranch,
  type DesignResult,
  type MachineNoun,
  type Ticks,
} from '../lib/designBranches'

/** "3 kiosks", in the design's machine noun. */
export function useMachines(noun: MachineNoun): (n: number) => string {
  const { say } = useStaffText()
  return (n) => say(`designs.use.${noun}`, { n })
}

/** The confirm lines: "Applies to 4 kiosks in 2 branches", and what is taken off. */
export function PlanNotice({ plan, noun }: { plan: BranchPlan; noun: MachineNoun }) {
  const { say } = useStaffText()
  const machines = useMachines(noun)
  return (
    <Notice tone="warning">
      {plan.addingBranches > 0 && (
        <span className="studio-notice__line">{say('designs.use.applies', { machines: machines(plan.adding), n: plan.addingBranches })}</span>
      )}
      {plan.removingBranches > 0 && (
        <span className="studio-notice__line">{say('designs.use.removes', { machines: machines(plan.removing), n: plan.removingBranches })}</span>
      )}
    </Notice>
  )
}

/** A branch's tick: on, off, or some of its machines (indeterminate). */
export function AssignRow({ tick, disabled, onChange, label, children }: { tick: string; disabled: boolean; onChange: () => void; label: string; children: ReactNode }) {
  return (
    <Checkbox
      className="studio-assign__row"
      variant="secondary"
      data-on={tick !== 'off' || undefined}
      aria-label={label}
      isSelected={tick === 'on'}
      isIndeterminate={tick === 'some'}
      isDisabled={disabled}
      onChange={onChange}
    >
      <Checkbox.Content>
        <Checkbox.Control>
          <Checkbox.Indicator />
        </Checkbox.Control>
        {children}
      </Checkbox.Content>
    </Checkbox>
  )
}

/** Each branch with a tick for "every machine of the kind shows this design". */
export function UseBranches({
  lines,
  ticks,
  noun,
  canApply,
  results,
  onToggle,
}: {
  lines: readonly DesignBranch[]
  ticks: Ticks
  noun: MachineNoun
  canApply: boolean
  results: Readonly<Record<string, DesignResult>> | null
  onToggle: (b: DesignBranch) => void
}) {
  const { say } = useStaffText()
  const machines = useMachines(noun)
  const on = lines.filter((b) => shownTick(b, ticks) !== 'off').length
  return (
    <details className="studio-assign" open>
      <summary>
        {say('designs.use.branches')} <span className="studio-muted">{on}</span>
      </summary>
      <ul className="studio-assign__list">
        {lines.map((b) => {
          const tick = shownTick(b, ticks)
          const ready = b.status === 'ready'
          const result = results?.[b.branchId]
          const count =
            tick === 'some' ? say('designs.use.someOf', { on: b.onDesign, all: machines(b.machines) }) : machines(b.machines)
          return (
            <li key={b.branchId}>
              <AssignRow tick={tick} disabled={!ready || !canApply} onChange={() => onToggle(b)} label={b.branchName}>
                <span className="studio-assign__name">{b.branchName}</span>
                <span className="studio-muted studio-assign__code">{b.branchCode}</span>
                {result ? (
                  <Chip size="sm" variant="soft" color={succeeded(result) ? 'default' : 'warning'} className="studio-assign__via" title={result.detail}>
                    {say(resultKey(result))}
                  </Chip>
                ) : b.status === 'ready' ? (
                  <span className="studio-muted studio-assign__via">{count}</span>
                ) : (
                  <Chip size="sm" variant="soft" className="studio-assign__via">
                    {b.status === 'none' ? machines(0) : say(statusKey(b.status))}
                  </Chip>
                )}
              </AssignRow>
            </li>
          )
        })}
      </ul>
    </details>
  )
}

function statusKey(s: Exclude<BranchStatus, 'ready'>): StaffKey {
  return `designs.use.status.${s}`
}

/** A refusal for the branch's own state reads as that state. */
function resultKey(r: DesignResult): StaffKey {
  switch (r.outcome) {
    case 'pending':
    case 'never':
    case 'profile':
    case 'none':
    case 'inactive':
      return statusKey(r.outcome)
  }
  return `designs.use.outcome.${r.outcome}`
}
